import { useCallback, useEffect, useMemo, useState } from 'react'
import { IconButton, Tooltip, Box, Dialog } from '@mui/material'
import Undo from '@mui/icons-material/Undo'
import History from '@mui/icons-material/History'
import { displayNamesOfEventTypes } from 'src/consumer/constants'
import ClinicScreeningFallbackDetails from 'src/mentor/components/MentorEventsPage/components/eventsListing/ClinicScreeningFallbackDetails'
import EventTypeCreationForm from 'src/mentor/components/MentorEventsPage/components/eventsListing/EventTypeCreationForm'
import EventTypeFormDrawer from 'src/mentor/components/MentorEventsPage/components/eventsListing/EventTypeFormDrawer'
import TherapistEventsListing from 'src/mentor/components/MentorEventsPage/components/eventsListing/TherapistEventsListing'
import { getAllBundlePacksForCurrentMentor } from 'src/mentor/components/MentorEventsPage/MentorEventsHelpers'
import { buildEffectiveClinicScreeningFallbacks } from 'src/mentor/components/MentorEventsPage/components/utils'
import {
  getClinicBrandIdFromClinicId,
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
  sleep,
} from 'src/mentor/helpers/utilityFunctions'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import { getSPDataWithParams, postSPDataWithParams } from 'src/supportPortal/services/services'
import {
  getAllOneOnOnePlansForSupportPortal,
  getMinimalMentorData,
} from 'src/supportPortal/views/therapists/util'
import RevertEventsDialog from './RevertEventsDialog'
import { toast } from 'react-toastify'

export default function MentorEventTypes({
  mentorSlug,
  fetchSingleMentorSignature,
  saveOneOnOnePlanApiSignature,
  fetchMentorClinicAssociationsSignature = '/api/secure/cc/support/therapists/clinic-associations',
  forDoctor = false,
  forTherapist = true,
}) {
  const [associatedClinics, setAssociatedClinics] = useState([])
  const [currentTherapist, setCurrentTherapist] = useState()
  const [isLoading, setIsLoading] = useState(true)
  const [selectedEventType, setSelectedEventType] = useState()
  const [showEventTypeModal, setShowEventTypeModal] = useState(false)
  const [oneOnOnePlans, setOneOnOnePlans] = useState([])
  const [bundlePacks, setBundlePacks] = useState([])
  const [originalEvents, setOriginalEvents] = useState([])
  const [currentVersion, setCurrentVersion] = useState(null)
  const [showRevertDialog, setShowRevertDialog] = useState(false)
  const [isReverting, setIsReverting] = useState(false)
  const [clinicAppointmentTypesMap, setClinicAppointmentTypesMap] = useState({})
  const [selectedClinicFallback, setSelectedClinicFallback] = useState(null)

  async function getAssociatedBrands() {
    const resp = await getSPDataWithParams(
      fetchMentorClinicAssociationsSignature,
      { 'mentor-slug': mentorSlug },
      undefined,
      undefined,
      true,
    )
    if (resp && isCommonResponseSuccessfulV2(resp)) {
      const dat = getDataMapFromCommonResponse(resp)?.clinic

      console.log({ dat })
      setAssociatedClinics(dat)
      return dat || []
    }
    return []
  }

  async function getSelectedTherapist() {
    const resp = await getMinimalMentorData(mentorSlug, true, fetchSingleMentorSignature)
    setCurrentTherapist(resp)
    // Store original events to track deletions
    if (resp?.eventTypes) {
      setOriginalEvents([...resp.eventTypes])
    }
    return resp
  }

  async function getClinicAppointmentTypesForSupportMentor(clinics, mentorId) {
    if (!Array.isArray(clinics) || clinics.length === 0 || !mentorId) {
      setClinicAppointmentTypesMap({})
      return
    }

    const clinicAppointmentEntries = await Promise.all(
      clinics
        .filter((clinic) => clinic?.id && clinic?.clinicBrandId && clinic?.useClinicAppointmentTypesForBooking)
        .map(async (clinic) => {
          const resp = await getSPDataWithParams(
            '/api/secure/cc/support/clinic/clinic-appointment-type/list',
            {
              clinicLocationId: clinic?.id,
              clinicBrandId: clinic?.clinicBrandId,
              mentorId,
            },
            undefined,
            undefined,
            true,
          )

          if (resp && isCommonResponseSuccessfulV2(resp)) {
            const data = getDataMapFromCommonResponse(resp)
            return [clinic.id, data?.appointmentTypes || []]
          }
          return [clinic.id, []]
        }),
    )

    setClinicAppointmentTypesMap(Object.fromEntries(clinicAppointmentEntries))
  }

  async function getOneOnOnePlans() {
    const oneOnOnePlans = await getAllOneOnOnePlansForSupportPortal(mentorSlug)
    if (oneOnOnePlans) {
      setOneOnOnePlans(oneOnOnePlans)
    }
  }

  async function getBundlePackPlans(mentorId = currentTherapist?.id) {
    const bundlePacks = await getAllBundlePacksForCurrentMentor(mentorId)
    if (bundlePacks) {
      setBundlePacks(bundlePacks)
    }
  }

  async function saveEventTypes(events, note = '') {
    console.log({ events })
    
    // Detect deleted events by comparing with original events
    const originalEventUids = new Set(originalEvents.map((ev) => ev?.uid).filter(Boolean))
    const currentEventUids = new Set(events.map((ev) => ev?.uid).filter(Boolean))
    const deleteEventUids = Array.from(originalEventUids).filter((uid) => !currentEventUids.has(uid))
    
    // Prepare events for API (ensure apiParam is set to 'event-type' for proper deserialization)
    const eventsForApi = events.map((ev) => {
      return {
        ...ev,
        apiParam: 'event-type',
      }
    })
    
    // Prepare request DTO according to v2 API specification
    const requestBody = {
      mentorSlug,
      events: eventsForApi,
      deleteEventUids: deleteEventUids.length > 0 ? deleteEventUids : undefined,
      ...(note && { note }),
    }
    
    // Remove undefined fields
    Object.keys(requestBody).forEach((key) => {
      if (requestBody[key] === undefined) {
        delete requestBody[key]
      }
    })
    
    const resp = await postSPDataWithParams(
      '/api/secure/cc/support/update/events/v2',
      requestBody,
      {}, // No query params - mentorSlug is in body now
    )
    
    if (resp?.status === 200 && resp?.data?.code === 2000) {
      // Update version if provided in response
      if (resp.data?.data?.currentVersion) {
        setCurrentVersion(resp.data.data.currentVersion)
      }
      // Update original events to current state after successful save
      setOriginalEvents([...events])
      getSelectedTherapist()
    } else {
      console.error('Failed to save events:', resp?.data?.message || 'Unknown error')
    }
  }

  async function revertEventTypes(targetVersion = null, note = '') {
    setIsReverting(true)
    try {
      const requestBody = {
        mentorSlug,
        ...(targetVersion !== null && { targetVersion }),
        ...(note && { note }),
      }
      
      // Remove undefined fields
      Object.keys(requestBody).forEach((key) => {
        if (requestBody[key] === undefined) {
          delete requestBody[key]
        }
      })
      
      const resp = await postSPDataWithParams(
        '/api/secure/cc/support/update/events/revert',
        requestBody,
        {}, // No query params
      )
      
      if (resp?.status === 200 && resp?.data?.code === 2000) {
        // Update version if provided in response
        if (resp.data?.data?.currentVersion) {
          setCurrentVersion(resp.data.data.currentVersion)
        }
        // Update original events after successful revert
        await getSelectedTherapist()
        toast.success(
          `Successfully reverted to version ${resp.data?.data?.restoredFromVersion || 'previous'}`,
        )
        return true
      } else {
        const errorMessage = resp?.data?.message || 'Unknown error'
        console.error('Failed to revert events:', errorMessage)
        toast.error(`Failed to revert: ${errorMessage}`)
        return false
      }
    } catch (error) {
      console.error('Error reverting events:', error)
      toast.error('An error occurred while reverting events')
      return false
    } finally {
      setIsReverting(false)
    }
  }

  const handleRevert = async (targetVersion, note) => {
    await revertEventTypes(targetVersion, note)
  }

  async function Setup() {
    setIsLoading(true)
    const [mentor, clinics] = await Promise.all([getSelectedTherapist(), getAssociatedBrands()])
    await getClinicAppointmentTypesForSupportMentor(clinics, mentor?.id)
    await sleep(500)
    await Promise.all([getOneOnOnePlans(), getBundlePackPlans(mentor?.id)])
    setIsLoading(false)
  }

  const effectiveClinicScreeningEntries = useMemo(
    () =>
      buildEffectiveClinicScreeningFallbacks({
        mentorEventTypes: currentTherapist?.eventTypes,
        associatedClinics,
        clinicAppointmentTypesMap,
      }),
    [associatedClinics, clinicAppointmentTypesMap, currentTherapist?.eventTypes],
  )

  const closeEventTypeDrawer = useCallback(() => {
    setShowEventTypeModal(false)
    setSelectedEventType(null)
  }, [])

  const setShowEventTypeModalSafe = useCallback(
    (open) => {
      if (open === false) {
        closeEventTypeDrawer()
      } else {
        setShowEventTypeModal(true)
      }
    },
    [closeEventTypeDrawer],
  )

  const handleEventCreation = async (data) => {
    if (data?.contextId) {
      data.clinicBrandId = getClinicBrandIdFromClinicId(associatedClinics, data?.contextId)
    } else {
      data.clinicBrandId = ''
    }

    const prevEvents = currentTherapist?.eventTypes || []
    let events = [...prevEvents]

    if (data?.uid) {
      const indexOfEvent = events.findIndex((item) => data.uid === item.uid)
      if (indexOfEvent >= 0) {
        events[indexOfEvent] = data
      }
    } else {
      events = [...events, { ...data }]
    }

    setCurrentTherapist((prev) => ({
      ...prev,
      eventTypes: events,
    }))

    // Generate a note for audit purposes
    const note = data?.uid
      ? `Updated event: ${data?.title || data?.uid}`
      : `Created new event: ${data?.title || 'Untitled'}`

    await saveEventTypes(events, note)

    closeEventTypeDrawer()
  }

  useEffect(() => {
    Setup()
  }, [])
  return (
    <>
      <div style={{ position: 'sticky', top: '0px' }}>
        <SupportHeader heading={`${currentTherapist?.name} Events`}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {currentVersion !== null && (
              <Tooltip title={`Current Version: ${currentVersion}`}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1,
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                  }}
                >
                  <History sx={{ fontSize: 16 }} />
                  <span style={{ fontSize: '12px', fontWeight: 500 }}>v{currentVersion}</span>
                </Box>
              </Tooltip>
            )}
            <Tooltip title="Revert to Previous Version">
              <IconButton
                onClick={() => setShowRevertDialog(true)}
                sx={{
                  color: 'rgba(255, 255, 255, 0.95) !important',
                  backgroundColor: 'rgba(255, 152, 0, 0.2)',
                  backdropFilter: 'blur(10px)',
                  width: 40,
                  height: 40,
                  border: '1px solid rgba(255, 152, 0, 0.3)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 152, 0, 0.3)',
                    transform: 'scale(1.05)',
                    borderColor: 'rgba(255, 152, 0, 0.5)',
                  },
                }}
              >
                <Undo sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </SupportHeader>
      </div>
      <div>
        <TherapistEventsListing
          associatedClinics={associatedClinics}
          effectiveClinicScreeningEntries={effectiveClinicScreeningEntries}
          mentorEventTypes={currentTherapist?.eventTypes}
          mentorSlug={mentorSlug}
          onClickNewEventType={() => {
            setShowEventTypeModal(true)
            setSelectedEventType(null)
          }}
          onClickExistingEventType={(item) => {
            setShowEventTypeModal(true)
            setSelectedEventType(item)
          }}
          onClickClinicFallback={setSelectedClinicFallback}
        />
      </div>
      <EventTypeFormDrawer
        open={showEventTypeModal}
        onClose={closeEventTypeDrawer}
        title={selectedEventType ? 'Edit event type' : 'New event type'}
        subtitle="Configure session details, pricing, buffers, location, and clinic association. Saving writes mentor events through support."
      >
        {showEventTypeModal && currentTherapist && (
          <EventTypeCreationForm
            key={selectedEventType?.uid || selectedEventType?.id || 'new-event-type'}
            eventFormModalData={selectedEventType}
            setEventFormModalOpen={setShowEventTypeModalSafe}
            displayNamesOfEventTypes={displayNamesOfEventTypes}
            handleEventCreation={handleEventCreation}
            mentorClinicAssociationData={associatedClinics}
            mentorProfileData={currentTherapist}
            oneOnOnePlans={oneOnOnePlans}
            bundlePacks={bundlePacks}
            refreshBundlePacks={() => getBundlePackPlans(currentTherapist?.id)}
            refreshOneOnOnePlans={getOneOnOnePlans}
            forceShowTherapistEventTypes={forTherapist}
            forceShowDoctorEventTypes={forDoctor}
            saveOneOnOnePlanApi={`${saveOneOnOnePlanApiSignature}?mentor-id=${currentTherapist?.id}`}
            showNextVisitSlotSelectorEnabler
            layout="drawer"
          />
        )}
      </EventTypeFormDrawer>
      <Dialog open={Boolean(selectedClinicFallback)} onClose={() => setSelectedClinicFallback(null)}>
        <ClinicScreeningFallbackDetails
          fallbackEntry={selectedClinicFallback}
          onClose={() => setSelectedClinicFallback(null)}
        />
      </Dialog>
      <RevertEventsDialog
        open={showRevertDialog}
        onClose={() => setShowRevertDialog(false)}
        onRevert={handleRevert}
        currentVersion={currentVersion}
        isLoading={isReverting}
      />
    </>
  )
}
